import { useCallback, useEffect, useRef, useState } from 'react'
import { PageLink, PageTitle } from '../../../_theme/layout/core'
import { Content } from '../../../_theme/layout/components/Content'
import { getProjectStages, ISortProject, IProjectStage } from '../../modules/project'
import { useParams } from 'react-router-dom'
import { ProjectStage } from '../../modules/project/ProjectStage'
import { useAuth } from '../../modules/auth'
import NotFound from '../../modules/apps/components/NotFound'
import Loader from '../../modules/apps/components/Loader'
import { useSnackbar } from '../../../_theme/context/SnackbarContext'
import {
    emptyDataValidation,
    envConfig,
    getDashboardStagegates
} from '../../../utils/config'
import GateStepper from '../../modules/project/components/GateStepper'
import { getPanopticEvaluationSummaryData } from '../../modules/core/_panoptic_evaluation'
import { IProjectEvaluationSummeryData } from '../../modules/core/_models'
import { getApiErrorMessage } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";

const ManageProject = () => {
    const data = Object.fromEntries(new URLSearchParams(window.location.search));

    const { auth } = useAuth();
    const { project_id } = useParams();
    const { showSnackbar } = useSnackbar();

    const [loading, setLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const [project, setProject] = useState<ISortProject | undefined>();
    const [stages, setStages] = useState<IProjectStage[]>([]);
    const [summeryData, setSummeryData] =
        useState<IProjectEvaluationSummeryData | null>(null);
    const stagesAbortRef = useRef<AbortController | null>(null);
    const summaryAbortRef = useRef<AbortController | null>(null);

    const fetchProjectStages = useCallback(async (
        project_id: string
    ) => {
        stagesAbortRef.current?.abort();
        const controller = new AbortController();
        stagesAbortRef.current = controller;

        try {
            setLoading(true);

            const projectStageResponse = await getProjectStages(project_id, controller.signal);

            if (controller.signal.aborted) return;

            if (projectStageResponse.status === 'ok') {
                setProject(projectStageResponse.data.project);
                setStages(projectStageResponse.data.stages);
                setErrorMessage(undefined);
            } else {
                setErrorMessage(projectStageResponse.message);
                showSnackbar(projectStageResponse.message, 'danger');
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error('Failed to fetch projects:', error);

            setErrorMessage(getApiErrorMessage(error, 'Failed to fetch projects'));

            showSnackbar(getApiErrorMessage(error, 'Failed to fetch projects'), 'danger');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [showSnackbar]);

    const handleGetPanopticEvaluationSummaryData = useCallback(async () => {
        if (emptyDataValidation.includes(project_id)) return;

        summaryAbortRef.current?.abort();
        const controller = new AbortController();
        summaryAbortRef.current = controller;

        try {
            const response =
                await getPanopticEvaluationSummaryData(Number(project_id), controller.signal);

            if (controller.signal.aborted) return;

            if (response.status === 'ok') {
                setSummeryData(response.data);
            } else {
                showSnackbar(response.message, 'danger');
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error(error);
        }
    }, [project_id, showSnackbar]);

    useEffect(() => {
        handleGetPanopticEvaluationSummaryData();
        return () => summaryAbortRef.current?.abort();
    }, [handleGetPanopticEvaluationSummaryData]);

    useEffect(() => {
        if (project_id) {
            fetchProjectStages(project_id);
        }
        return () => stagesAbortRef.current?.abort();
    }, [project_id, fetchProjectStages]);

    // Loader
    if (loading) {
        return <Loader loadingText='Loading projects…' />;
    }

    // Not Found
    if (!project || errorMessage) {
        return (
            <NotFound
                notFoundText={errorMessage}
                notFoundTitle='Unauthorized Access !'
            />
        );
    }

    const breadCrumbs: Array<PageLink> = [
        {
            title: 'My Projects',
            path:
                data.label &&
                    data.dashboard &&
                    data.url &&
                    data.filter
                    ? `/projects/?label=${data.label}&dashboard=${data.dashboard}&url=${data.url}&filter=${data.filter}`
                    : getDashboardStagegates(),
            isSeparator: false,
            isActive: false,
        },
        {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
        },
        {
            title: 'Project Stages',
            path: '',
            isSeparator: false,
            isActive: false,
        },
    ];

    const description = (
        <div className='d-flex align-items-center fs-6'>
            <span>{project.sector_name}</span>

            <span className='bullet bg-gray-800 opacity-75 w-5px h-2px mx-1'></span>

            <span>{project.department_name}</span>

            <span className='bullet bg-gray-800 opacity-75 w-5px h-2px mx-1'></span>

            <span>{project.location_name}</span>
        </div>
    );

    return (
        <>
            <PageTitle
                sitetitle={envConfig.APP_GOV_TITLE}
                breadcrumbs={breadCrumbs}
                description={description}
            >
                {project.name}
            </PageTitle>

            <Content>
                <ProjectStage
                    user_id={auth?.user_id || ''}
                    project_id={project.project_id}
                    stages={stages}
                    department_name={project.department_name}
                    project={project}
                    summeryData={summeryData}
                />
            </Content>
        </>
    );
};

export { ManageProject };